import styles from './index.module.scss';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { message, Modal } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import classNames from 'classnames';
import {
  apiV2ElectricMapDataGgdDataPost,
  apiV2GgdBatchOperateStatusPost,
  apiV2LightmapAreaPost,
  apiV2LightmapSunlightDevicePagePost,
  V2ElectricMapDataGgdDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import LeftBox from './components/LeftBox';
import RightBox from './components/RightBox';
import UnityMap from './components/UnityMap';
import MapView from './components/MapView';
import CenterTopBox from './components/CenterTopBox';
import WorkshopPanel from './components/WorkshopLightPanel';
import GgdLightModal from './components/GgdLightModal';
import SunLightModal, { SunLightPopupData } from './components/SunLightModal';
import { UnityClickDetail } from '../UnityRenderMap';
import type { WgqMapMode } from '../..';
import { ShareDataWrapperContext } from '../..';
import mockAreaData from './mockAreaData.json';

const parseDeviceIds = (value: unknown): number[] => {
  if (Array.isArray(value)) return value.map(Number).filter(Number.isFinite);
  if (typeof value !== 'string' || !value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
  } catch (error) {
    console.warn('[UnityLightMap] parse ggdDeviceIds failed', { value, error });
    return [];
  }
};

const UnityLightMap: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const { isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const [workSpaceLightVisible, setWorkSpaceLightVisible] = useState(false);
  const [workshopName, setWorkshopName] = useState<string>();
  const [lightPopupData, setLightPopupData] = useState<V2ElectricMapDataGgdDataPostResponse>();
  const [lightPopupDeviceIds, setLightPopupDeviceIds] = useState<number[]>([]);
  const [sunLightPopupData, setSunLightPopupData] = useState<SunLightPopupData>();
  const [activeLightModal, setActiveLightModal] = useState<'ggd' | 'sun'>();
  const [lightPopupLoading, setLightPopupLoading] = useState(false);
  const popupRequestIdRef = useRef(0);

  const resetPopupState = useCallback(() => {
    popupRequestIdRef.current += 1;
    setWorkshopName(undefined);
    setWorkSpaceLightVisible(false);
    setActiveLightModal(undefined);
    setLightPopupLoading(false);
    setLightPopupData(undefined);
    setLightPopupDeviceIds([]);
    setSunLightPopupData(undefined);
  }, []);

  // 查询区域位置数据
  const { data: areaList } = useRequest(async () => {
    const res = await apiV2LightmapAreaPost({});
    return res?.list ?? [];
  });

  const handleUnityClick = useCallback(
    (detail: UnityClickDetail) => {
      if (!detail.uuid) return;
      resetPopupState();
      const isArea = detail?.data?.name && !detail?.data?.id;

      if (isArea) {
        setWorkshopName(detail?.data?.name);
        setWorkSpaceLightVisible(true);
        return;
      }

      const clickedLight = areaList?.find(k => k.id === detail?.data?.id);
      if (clickedLight && clickedLight.existSunlight) {
        const requestId = clickedLight.id;

        const popupRequestId = ++popupRequestIdRef.current;
        setActiveLightModal('sun');
        setLightPopupLoading(true);
        setSunLightPopupData(undefined);
        apiV2LightmapSunlightDevicePagePost({
          id: requestId,
          deviceIds: clickedLight?.lightDeviceIds ?? [],
          page: 1,
          size: 1,
        })
          .then(res => {
            if (popupRequestId !== popupRequestIdRef.current) return;
            setSunLightPopupData(res?.list?.[0]);
            setLightPopupLoading(false);
            setWorkshopName(undefined);
            setWorkSpaceLightVisible(false);
          })
          .catch(() => {
            if (popupRequestId === popupRequestIdRef.current) setLightPopupLoading(false);
          });

        return;
      }

      // 点击高杆灯浮窗：保持原有高杆灯弹窗逻辑
      if (clickedLight && clickedLight.existGgd) {
        const popupRequestId = ++popupRequestIdRef.current;
        setActiveLightModal('ggd');
        setLightPopupLoading(true);
        apiV2ElectricMapDataGgdDataPost({ id: clickedLight.id })
          .then(res => {
            if (popupRequestId !== popupRequestIdRef.current) return;
            setLightPopupData(res);
            setLightPopupDeviceIds(parseDeviceIds(clickedLight.ggdDeviceIds));
            setLightPopupLoading(false);
            setWorkshopName(undefined);
            setWorkSpaceLightVisible(false);
          })
          .catch(() => {
            if (popupRequestId === popupRequestIdRef.current) setLightPopupLoading(false);
          });
        return;
      }
    },
    [areaList, resetPopupState]
  );

  const closeLightModal = useCallback(() => {
    resetPopupState();
  }, [resetPopupState]);

  const closeSunLightModal = useCallback(() => {
    resetPopupState();
  }, [resetPopupState]);

  const handleOpenSunLightHistory = useCallback(() => {
    if (!sunLightPopupData?.deviceId) return;
    window.open(`/data/history/device?deviceId=${sunLightPopupData.deviceId}`, '_blank');
  }, [sunLightPopupData?.deviceId]);

  const handleMarkerVisibleChange = useCallback(
    (type: 'ggdLight' | 'sunLight' | 'workshopLight', visible: boolean) => {
      if (visible) return;

      if (type === 'ggdLight') {
        closeLightModal();
        return;
      }
      if (type === 'sunLight') {
        closeSunLightModal();
        return;
      }

      setWorkshopName(undefined);
      setWorkSpaceLightVisible(false);
    },
    [closeLightModal, closeSunLightModal]
  );

  const handleToggleLightStatus = useCallback(() => {
    if (!lightPopupData) return;
    if (!lightPopupDeviceIds.length) {
      message.warning('未获取到高杆灯设备ID');
      return;
    }

    Modal.confirm({
      title: (
        <span style={{ color: 'var(--mx-text-base-color)' }}>
          确定要下发
          <span style={{ color: '#E64242' }}>{lightPopupData.intStatus === 1 ? '远程关闭' : '远程开启'}</span>
          指令到{lightPopupData.name}设备吗？
        </span>
      ),
      okText: '确定',
      onOk() {
        const newStatus = lightPopupData.intStatus === 1 ? 0 : 1;
        return apiV2GgdBatchOperateStatusPost({
          deviceIds: lightPopupDeviceIds,
          status: newStatus,
        }).then(() => {
          message.success('下发成功');
          setLightPopupData(prev => (prev ? { ...prev, intStatus: newStatus } : prev));
        });
      },
    });
  }, [lightPopupData, lightPopupDeviceIds]);

  const MapComponent = (mapMode === 'unityMap' ? UnityMap : MapView) as React.ComponentType<any>;
  const showMapOnly = isStandardSize && loadMap;

  return (
    <div className={classNames(styles.inner_center, !loadMap ? styles.inner_center_no_map : '')}>
      {loadMap && (
        <MapComponent
          className={styles.map}
          areaList={areaList}
          onUnityClick={handleUnityClick}
          onMarkerVisibleChange={handleMarkerVisibleChange}
        />
      )}
      {!showMapOnly && (
        <>
          <CenterTopBox className={classNames(styles.centerBox, !loadMap ? styles.centerBoxNoMap : '')} />
          <div className={classNames(styles.content_layer, !loadMap ? styles.content_layer_no_map : '')}>
            <div className={classNames(styles.center_left_box, !loadMap ? styles.noMapWidth : '')}>
              <LeftBox />
            </div>
            <div className={classNames(styles.center_right_box, !loadMap ? styles.noMapWidth : '')}>
              <RightBox />
            </div>
          </div>
        </>
      )}
      <Modal
        open={workSpaceLightVisible}
        onCancel={() => {
          setWorkshopName(undefined);
          setWorkSpaceLightVisible(false);
        }}
        width={1200}
        footer={null}
        className={classNames(styles.modal_sty, showMapOnly ? styles.standard_map_modal_sty : '')}
        destroyOnClose
        getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      >
        <WorkshopPanel zoneName={workshopName} workSpaceLightVisible={workSpaceLightVisible} />
      </Modal>
      <GgdLightModal
        open={activeLightModal === 'ggd'}
        loading={lightPopupLoading}
        data={lightPopupData}
        onClose={closeLightModal}
        onToggleStatus={handleToggleLightStatus}
      />
      <SunLightModal
        open={activeLightModal === 'sun'}
        loading={lightPopupLoading}
        data={sunLightPopupData}
        onClose={closeSunLightModal}
        onOpenHistory={handleOpenSunLightHistory}
      />
    </div>
  );
};

export default UnityLightMap;
